// src/pages/ImpiantiPage.js
// Anagrafica impianti — un cliente può avere molti impianti (cabine MT/BT, quadri, impianti
// di terra, colonnine di ricarica, impianti fotovoltaici...), ciascuno con la propria
// ubicazione e i propri dati tecnici.
//
// È la fondazione su cui poggeranno le verifiche periodiche obbligatorie (DPR 462/01):
// una verifica non si fa "al cliente", si fa su un impianto preciso, in un sito preciso.
// Per questo l'impianto è un'entità a sé e non un campo dentro il cliente.
//
// Non duplica i dati del cliente: li eredita tramite clienteId, così un cambio di ragione
// sociale non lascia dietro impianti con il nome vecchio.
import React, { useEffect, useState, useMemo } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useCanSee } from "../hooks/useCanSee";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { messaggioErrore } from "../utils/erroriUtente";
import { logModifica } from "../utils/logModifica";
import { esportaXLSX } from "../utils/esportaXLSX";
import { Plus, X, Zap, Search, Trash2, Building2 } from "lucide-react";
import { SelettoreSede } from "../components/SediCliente";
import { copiaLibera } from "../utils/sediCliente";

// Tipologie di partenza — modificabili in futuro senza toccare il codice: sono solo i
// suggerimenti della tendina, il campo accetta comunque testo libero perché ogni azienda
// ha le proprie denominazioni.
export const TIPOLOGIE_IMPIANTO = [
  "Cabina di trasformazione MT/BT",
  "Cabina di ricezione MT",
  "Impianto di terra",
  "Impianto di protezione scariche atmosferiche",
  "Quadro elettrico generale",
  "Quadro elettrico di zona",
  "Impianto fotovoltaico",
  "Colonnina di ricarica veicoli elettrici",
  "Gruppo elettrogeno",
  "Impianto di illuminazione",
  "Impianto speciale (TVCC, antintrusione, rilevazione incendi)",
  "Altro",
];

const inp = { border:"1px solid #e5e7eb", borderRadius:7, padding:"8px 10px", fontSize:13, fontFamily:"'Inter',sans-serif", outline:"none", width:"100%", boxSizing:"border-box" };
const lbl = { fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 };

function formVuoto() {
  return { clienteId:"", denominazione:"", tipologia:"", ubicazione:"", indirizzo:"",
    matricola:"", costruttore:"", annoInstallazione:"", potenzaKw:"", tensione:"",
    referenteSito:"", telefonoSito:"", note:"", statoImpianto:"attivo" };
}

function ImpiantoModal({ impianto, clienti, orgId, currentUser, onClose }) {
  const { addToast } = useToast();
  const [form, setForm] = useState(() => impianto ? {...formVuoto(), ...impianto} : formVuoto());
  const [saving, setSaving] = useState(false);
  const upd = (k,v) => setForm(f=>({...f,[k]:v}));

  const salva = async () => {
    if (!form.denominazione.trim()) { addToast("La denominazione dell'impianto è obbligatoria","error"); return; }
    if (!form.clienteId) { addToast("Seleziona il cliente a cui appartiene l'impianto","error"); return; }
    if (saving) return;
    setSaving(true);
    try {
      const cliente = clienti.find(c=>c.id===form.clienteId);
      const dati = {
        ...form,
        denominazione: form.denominazione.trim(),
        // Il nome del cliente si salva solo come comodità di lettura negli elenchi: la fonte
        // vera resta clienteId, così una modifica in anagrafica clienti non lascia impianti
        // agganciati a un nome ormai vecchio.
        clienteNome: cliente?.ragioneSociale || cliente?.nome || "",
        organizationId: orgId,
        updatedAt: serverTimestamp(),
      };
      if (impianto?.id) {
        await updateDoc(doc(db,"impianti",impianto.id), dati);
        logModifica({ orgId, uid:currentUser.uid, nomeUtente:currentUser.displayName||"Utente",
          sezione:"impianti", refId:impianto.id, refNome:dati.denominazione,
          campo:"impianto", campoLabel:"Impianto modificato", before:impianto.denominazione, after:dati.denominazione });
        addToast("Impianto aggiornato ✅","success");
      } else {
        const ref = await addDoc(collection(db,"impianti"), { ...dati, createdAt: serverTimestamp(), createdBy: currentUser.uid });
        logModifica({ orgId, uid:currentUser.uid, nomeUtente:currentUser.displayName||"Utente",
          sezione:"impianti", refId:ref.id, refNome:dati.denominazione,
          campo:"impianto", campoLabel:"Impianto creato", before:"", after:dati.denominazione });
        addToast("Impianto creato ✅","success");
      }
      onClose();
    } catch(err) { addToast(messaggioErrore(err),"error"); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:720, maxHeight:"90vh", overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:700}}>⚡ {impianto?.id ? "Modifica impianto" : "Nuovo impianto"}</h3>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label style={lbl}>Cliente *</label>
            <select value={form.clienteId} onChange={e=>upd("clienteId",e.target.value)} style={inp}>
              <option value="">— Seleziona cliente —</option>
              {clienti.map(c => <option key={c.id} value={c.id}>{c.ragioneSociale||c.nome||"Cliente"}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Denominazione impianto *</label>
            <input value={form.denominazione} onChange={e=>upd("denominazione",e.target.value)} style={inp}
              placeholder="Es. Cabina MT/BT stabilimento nord"/>
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label style={lbl}>Tipologia</label>
            <input list="tipologie-impianto" value={form.tipologia} onChange={e=>upd("tipologia",e.target.value)} style={inp}
              placeholder="Scegli o scrivi liberamente"/>
            <datalist id="tipologie-impianto">
              {TIPOLOGIE_IMPIANTO.map(t=><option key={t} value={t}/>)}
            </datalist>
          </div>
          <div>
            <label style={lbl}>Posizione nella sede (facoltativa)</label>
            <input value={form.ubicazione} onChange={e=>upd("ubicazione",e.target.value)} style={inp}
              placeholder="Es. Locale tecnico, piano interrato"/>
          </div>
        </div>

        <div style={{marginBottom:12}}>
          {/* Sede del cliente dove si trova l'impianto (anagrafica cliente → Sedi e siti) */}
          <SelettoreSede clienteId={form.clienteId||""} etichetta="Sede del cliente dove si trova l'impianto"
            value={form.sede || (form.indirizzo ? copiaLibera(form.indirizzo) : null)}
            onChange={c=>{ upd("sede", c||null); upd("indirizzo", c?.indirizzo||""); }}/>
        </div>

        <div style={{fontSize:12,fontWeight:700,color:"#374151",margin:"18px 0 8px"}}>Dati tecnici (facoltativi)</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label style={lbl}>Matricola / N° serie</label>
            <input value={form.matricola} onChange={e=>upd("matricola",e.target.value)} style={inp}/>
          </div>
          <div>
            <label style={lbl}>Costruttore</label>
            <input value={form.costruttore} onChange={e=>upd("costruttore",e.target.value)} style={inp}/>
          </div>
          <div>
            <label style={lbl}>Anno installazione</label>
            <input value={form.annoInstallazione} onChange={e=>upd("annoInstallazione",e.target.value)} style={inp} placeholder="Es. 2018"/>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label style={lbl}>Potenza (kW)</label>
            <input value={form.potenzaKw} onChange={e=>upd("potenzaKw",e.target.value)} style={inp}/>
          </div>
          <div>
            <label style={lbl}>Tensione</label>
            <input value={form.tensione} onChange={e=>upd("tensione",e.target.value)} style={inp} placeholder="Es. 20 kV / 400 V"/>
          </div>
        </div>

        <div style={{fontSize:12,fontWeight:700,color:"#374151",margin:"18px 0 8px"}}>Riferimento sul posto</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label style={lbl}>Referente del sito</label>
            <input value={form.referenteSito} onChange={e=>upd("referenteSito",e.target.value)} style={inp}
              placeholder="Chi apre e accompagna in cabina"/>
          </div>
          <div>
            <label style={lbl}>Telefono referente</label>
            <input value={form.telefonoSito} onChange={e=>upd("telefonoSito",e.target.value)} style={inp}/>
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 200px",gap:12,marginBottom:16}}>
          <div>
            <label style={lbl}>Note</label>
            <input value={form.note} onChange={e=>upd("note",e.target.value)} style={inp}
              placeholder="Accessi, chiavi, particolarità, avvertenze"/>
          </div>
          <div>
            <label style={lbl}>Stato</label>
            <select value={form.statoImpianto} onChange={e=>upd("statoImpianto",e.target.value)} style={inp}>
              <option value="attivo">Attivo</option>
              <option value="dismesso">Dismesso</option>
            </select>
          </div>
        </div>

        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" onClick={salva} disabled={saving}>{saving?"Salvo...":"Salva impianto"}</button>
        </div>
      </div>
    </div>
  );
}

export default function ImpiantiPage({ onNavigate }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const canSee = useCanSee();
  const { addToast } = useToast();
  const [impianti, setImpianti] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [cerca, setCerca] = useState("");
  const [filtroCliente, setFiltroCliente] = useState("tutti");
  const [filtroStato, setFiltroStato] = useState("attivi");
  const [modalTarget, setModalTarget] = useState(null); // null = chiuso, {} = nuovo, {id...} = modifica

  const canEdit = isOrgAdmin || isPlatformOwner || canSee("gestione-impianti");

  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"impianti"), where("organizationId","==",effectiveOrgId)),
      snap => setImpianti(snap.docs.map(d=>({id:d.id,...d.data()}))));
  },[effectiveOrgId]);

  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"clienti"), where("organizationId","==",effectiveOrgId)),
      snap => setClienti(snap.docs.map(d=>({id:d.id,...d.data()}))
        .sort((a,b)=>(a.ragioneSociale||a.nome||"").localeCompare(b.ragioneSociale||b.nome||"","it"))));
  },[effectiveOrgId]);

  const filtrati = useMemo(()=>{
    const q = cerca.trim().toLowerCase();
    return impianti
      .filter(i => filtroStato==="tutti" || (filtroStato==="attivi" ? i.statoImpianto!=="dismesso" : i.statoImpianto==="dismesso"))
      .filter(i => filtroCliente==="tutti" || i.clienteId===filtroCliente)
      .filter(i => !q || [i.denominazione,i.tipologia,i.ubicazione,i.clienteNome,i.matricola,i.indirizzo]
        .some(v => (v||"").toLowerCase().includes(q)))
      .sort((a,b)=>(a.clienteNome||"").localeCompare(b.clienteNome||"","it") || (a.denominazione||"").localeCompare(b.denominazione||"","it"));
  },[impianti, cerca, filtroCliente, filtroStato]);

  // Quanti impianti per cliente — l'informazione che rende evidente il rapporto uno-a-molti
  const perCliente = useMemo(()=>{
    const m = {};
    impianti.filter(i=>i.statoImpianto!=="dismesso").forEach(i=>{ m[i.clienteId]=(m[i.clienteId]||0)+1; });
    return m;
  },[impianti]);

  const elimina = async (imp) => {
    if (!await confirm(`Eliminare l'impianto "${imp.denominazione}"? L'operazione non è reversibile.`, { type:"danger" })) return;
    try {
      await deleteDoc(doc(db,"impianti",imp.id));
      logModifica({ orgId:effectiveOrgId, uid:currentUser.uid, nomeUtente:currentUser.displayName||"Utente",
        sezione:"impianti", refId:imp.id, refNome:imp.denominazione,
        campo:"impianto", campoLabel:"Impianto eliminato", before:imp.denominazione, after:"" });
      addToast("Impianto eliminato","success");
    } catch(err) { addToast(messaggioErrore(err),"error"); }
  };

  const esporta = () => {
    if (filtrati.length===0) { addToast("Nessun impianto da esportare con i filtri attuali","warning"); return; }
    const intestazione = ["Cliente","Denominazione","Tipologia","Sito/Ubicazione","Indirizzo","Matricola","Costruttore","Anno","Potenza (kW)","Tensione","Referente sito","Telefono","Stato","Note"];
    const righe = filtrati.map(i=>[i.clienteNome||"—", i.denominazione||"—", i.tipologia||"—", i.ubicazione||"—", i.indirizzo||"—",
      i.matricola||"—", i.costruttore||"—", i.annoInstallazione||"—", i.potenzaKw||"—", i.tensione||"—",
      i.referenteSito||"—", i.telefonoSito||"—", i.statoImpianto==="dismesso"?"Dismesso":"Attivo", i.note||""]);
    esportaXLSX([intestazione,...righe], `Impianti_${new Date().toLocaleDateString("it-IT").replace(/\//g,"-")}`, "Impianti");
  };

  return (
    <div style={{padding:24}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6,flexWrap:"wrap",gap:10}}>
        <div>
          <h2 style={{fontSize:18,fontWeight:800,color:"#1e1b4b",display:"flex",alignItems:"center",gap:8}}>
            <Zap size={19} color="#7c3aed"/> Anagrafica Impianti
          </h2>
          <p style={{fontSize:12,color:"#6b7280",marginTop:2}}>
            Gli impianti dei tuoi clienti — un cliente può averne quanti ne servono, ciascuno con il proprio sito e i propri dati tecnici.
          </p>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={esporta}
            style={{padding:"7px 14px",border:"1px solid #059669",borderRadius:8,background:"#f0fdf4",color:"#059669",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            📊 Excel
          </button>
          {canEdit && (
            <button onClick={()=>setModalTarget({})} className="btn btn-primary" style={{display:"flex",alignItems:"center",gap:6}}>
              <Plus size={14}/> Nuovo impianto
            </button>
          )}
        </div>
      </div>

      <div style={{display:"flex",gap:8,margin:"16px 0",flexWrap:"wrap",alignItems:"center"}}>
        <div style={{position:"relative",flex:1,minWidth:220}}>
          <Search size={13} style={{position:"absolute",left:10,top:10,color:"#9ca3af"}}/>
          <input value={cerca} onChange={e=>setCerca(e.target.value)} placeholder="Cerca per denominazione, cliente, sito, matricola..."
            style={{...inp, paddingLeft:30}}/>
        </div>
        <select value={filtroCliente} onChange={e=>setFiltroCliente(e.target.value)} style={{...inp, width:"auto", minWidth:200}}>
          <option value="tutti">Tutti i clienti</option>
          {clienti.map(c=>(
            <option key={c.id} value={c.id}>
              {c.ragioneSociale||c.nome||"Cliente"}{perCliente[c.id]?` (${perCliente[c.id]})`:""}
            </option>
          ))}
        </select>
        <select value={filtroStato} onChange={e=>setFiltroStato(e.target.value)} style={{...inp, width:"auto"}}>
          <option value="attivi">Solo attivi</option>
          <option value="dismessi">Solo dismessi</option>
          <option value="tutti">Tutti</option>
        </select>
      </div>

      {filtrati.length===0 ? (
        <div style={{textAlign:"center",padding:56,color:"#9ca3af"}}>
          <Building2 size={38} style={{marginBottom:12,opacity:.3}}/>
          <div style={{fontSize:14,fontWeight:600}}>Nessun impianto {impianti.length>0 ? "con questi filtri" : "ancora censito"}</div>
          {impianti.length===0 && canEdit && (
            <div style={{fontSize:12.5,marginTop:6}}>Comincia dal primo: premi «Nuovo impianto» e collegalo al suo cliente.</div>
          )}
        </div>
      ) : (
        <div style={{border:"1px solid #e5e7eb",borderRadius:10,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12.5}}>
            <thead>
              <tr style={{background:"#f9fafb"}}>
                {["Cliente","Denominazione","Tipologia","Sito","Dati tecnici","Referente",""].map(h=>(
                  <th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:11,fontWeight:600,color:"#6b7280"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrati.map(i=>(
                <tr key={i.id} onClick={()=>canEdit&&setModalTarget(i)}
                  style={{borderTop:"1px solid #f3f4f6",cursor:canEdit?"pointer":"default",opacity:i.statoImpianto==="dismesso"?.55:1}}>
                  <td style={{padding:"9px 12px",color:"#6b7280"}}>{i.clienteNome||"—"}</td>
                  <td style={{padding:"9px 12px",fontWeight:700,color:"#111827"}}>
                    {i.denominazione}
                    {i.statoImpianto==="dismesso" && <span style={{marginLeft:6,fontSize:9.5,fontWeight:700,color:"#6b7280",background:"#f3f4f6",padding:"1px 6px",borderRadius:5}}>DISMESSO</span>}
                  </td>
                  <td style={{padding:"9px 12px",color:"#374151"}}>{i.tipologia||"—"}</td>
                  <td style={{padding:"9px 12px",color:"#6b7280"}}>
                    {i.ubicazione||"—"}
                    {i.indirizzo && <div style={{fontSize:11,color:"#9ca3af"}}>{i.indirizzo}</div>}
                  </td>
                  <td style={{padding:"9px 12px",fontSize:11,color:"#9ca3af"}}>
                    {[i.tensione, i.potenzaKw&&`${i.potenzaKw} kW`, i.matricola&&`matr. ${i.matricola}`].filter(Boolean).join(" · ")||"—"}
                  </td>
                  <td style={{padding:"9px 12px",fontSize:11,color:"#6b7280"}}>
                    {i.referenteSito||"—"}
                    {i.telefonoSito && <div style={{fontSize:10.5,color:"#9ca3af"}}>{i.telefonoSito}</div>}
                  </td>
                  <td style={{padding:"9px 12px",textAlign:"right"}}>
                    {canEdit && (
                      <button onClick={e=>{e.stopPropagation();elimina(i);}} title="Elimina impianto"
                        style={{background:"none",border:"none",cursor:"pointer",color:"#dc2626",padding:2}}>
                        <Trash2 size={13}/>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalTarget && (
        <ImpiantoModal impianto={modalTarget.id?modalTarget:null} clienti={clienti} orgId={effectiveOrgId}
          currentUser={currentUser} onClose={()=>setModalTarget(null)}/>
      )}
    </div>
  );
}
